import React, { useEffect, useState } from 'react';
import { Snackbar, Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tabs,
  Tab,
  TablePagination,
  Chip, } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { User, usersAPI, Company, companiesAPI } from '../../services/api';
import ExcelUpload from '../../components/ExcelUpload';
import { toPersianNumber } from '../../utils/persianNumber';

// Helper function to convert gender codes to Persian
const getGenderLabel = (gender: string | undefined | null): string => {
  if (!gender) return '-';
  const normalized = gender.toLowerCase().trim();
  if (normalized === 'm' || normalized === 'male') return 'آقا';
  if (normalized === 'f' || normalized === 'female') return 'خانم';
  return gender;
};

const ManagerUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit State
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    mobile: string;
    personnelCode: string;
    gender: string;
    company: string;
    companyId: string;
    cardNumber: string;
    workShift: 'day' | 'night';
  }>({
    firstName: '',
    lastName: '',
    mobile: '',
    personnelCode: '',
    gender: '',
    company: '',
    companyId: '',
    cardNumber: '',
    workShift: 'day',
  });

  useEffect(() => {
    loadUsers();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await companiesAPI.getCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersAPI.getUsers('staff');
      setUsers(data);
    } catch (err: any) {
      setError('خطا در بارگذاری کاربران');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('آیا از حذف این کاربر مطمئن هستید؟')) return;

    try {
      await usersAPI.deleteUser(userId);
      setSuccess('کاربر با موفقیت حذف شد');
      loadUsers();
    } catch (err: any) {
      setError('خطا در حذف کاربر');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      mobile: user.mobile,
      personnelCode: user.personnelCode || '',
      gender: user.gender || '',
      company: user.company || '',
      companyId: user.companyId || '',
      cardNumber: user.cardNumber || '',
      workShift: (user.workShift || 'day') as 'day' | 'night',
    });
    setOpenEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!editingUser) return;

    try {
      await usersAPI.updateUser(editingUser.id, {
        ...formData,
        role: 'staff', // Ensure role doesn't change
      });
      setSuccess('کاربر با موفقیت به‌روزرسانی شد');
      setOpenEditModal(false);
      setEditingUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        mobile: '',
        personnelCode: '',
        gender: '',
        company: '',
        companyId: '',
        cardNumber: '',
        workShift: 'day',
      });
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در به‌روزرسانی کاربر');
    }
  };


  const activeCount = users.filter((u) => u.isActive).length;
  const inactiveCount = users.filter((u) => !u.isActive).length;
  const tabUsers = users.filter((u) => (activeTab === 'active' ? u.isActive : !u.isActive));
  const paginatedUsers = tabUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            fontWeight: 600,
          }}
        >
          لیست پرسنل
        </Typography>
        <ExcelUpload onUploadSuccess={loadUsers} />
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      {/* Tabs for Active vs Inactive */}
      <Card sx={{ mb: 3, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => {
            setActiveTab(val);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            px: 2,
            minHeight: '52px',
            '& .MuiTab-root': {
              fontFamily: 'Vazir, sans-serif',
              fontSize: '15px',
              fontWeight: 600,
              minHeight: '52px',
              textTransform: 'none',
              px: 3,
            },
          }}
        >
          <Tab
            value="active"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>پرسنل فعال</span>
                <Chip
                  label={toPersianNumber(activeCount.toString())}
                  size="small"
                  color={activeTab === 'active' ? 'primary' : 'default'}
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    height: 24,
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '8px',
                  }}
                />
              </Box>
            }
          />
          <Tab
            value="inactive"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>پرسنل غیرفعال</span>
                <Chip
                  label={toPersianNumber(inactiveCount.toString())}
                  size="small"
                  color={activeTab === 'inactive' ? 'error' : 'default'}
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    height: 24,
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '8px',
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      </Card>

      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <TableContainer sx={{ bgcolor: '#fff', '& .MuiTableCell-root': { color: '#000', borderColor: 'rgba(0,0,0,0.1)' } }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, width: 60 }}>ردیف</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>کد پرسنلی</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>نام</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>نام خانوادگی</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>موبایل</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>جنسیت</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>شرکت</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>شماره کارت</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tabUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'Vazir, sans-serif' }}
                    >
                      {activeTab === 'active' ? 'هیچ پرسنل فعالی یافت نشد' : 'هیچ پرسنل غیرفعالی یافت نشد'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user, index) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber((page * rowsPerPage + index + 1).toString())}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.personnelCode ? toPersianNumber(user.personnelCode) : '-'}</TableCell>
                    <TableCell 
                      sx={{ 
                        fontFamily: 'Vazir, sans-serif',
                        color: 'primary.main',
                        cursor: 'pointer',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/admin/users/${user.id}/reports`)}
                      title="مشاهده گزارش و تمامی رزروهای این پرسنل"
                    >
                      {user.firstName}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontFamily: 'Vazir, sans-serif',
                        color: 'primary.main',
                        cursor: 'pointer',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/admin/users/${user.id}/reports`)}
                      title="مشاهده گزارش و تمامی رزروهای این پرسنل"
                    >
                      {user.lastName}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber(user.mobile)}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{getGenderLabel(user.gender)}</TableCell>
                     <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.companyTitle || user.company || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.cardNumber ? toPersianNumber(user.cardNumber) : '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => navigate(`/admin/users/${user.id}/reports`)}
                          sx={{ mr: 0.5 }}
                          title="مشاهده گزارش و رزروها"
                        >
                          <AssessmentIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(user)}
                          sx={{ mr: 0.5 }}
                          title="ویرایش"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(user.id)}
                          sx={{ ml: 0.5 }}
                          title="حذف"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={tabUsers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="تعداد در هر صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${toPersianNumber(from.toString())} تا ${toPersianNumber(to.toString())} از ${toPersianNumber(count.toString())}`
          }
          sx={{
            fontFamily: 'Vazir, sans-serif',
            direction: 'rtl',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
              fontFamily: 'Vazir, sans-serif',
            },
            '& .MuiTablePagination-actions': {
              direction: 'ltr',
            },
          }}
        />
      </Card>

      {/* Edit User Dialog */}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
          ویرایش پرسنل
        </DialogTitle>
        <Box component="form" onSubmit={handleUpdate}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              fullWidth
              label="نام خانوادگی"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              fullWidth
              label="شماره موبایل"
              value={formData.mobile}
              onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              fullWidth
              label="کد پرسنلی"
              value={formData.personnelCode}
              onChange={(e) => setFormData({ ...formData, personnelCode: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              select
              fullWidth
              label="جنسیت"
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              SelectProps={{ native: true }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            >
              <option value=""></option>
              <option value="male">مرد</option>
              <option value="female">زن</option>
            </TextField>
            <TextField
              select
              fullWidth
              label="شرکت"
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              SelectProps={{ native: true }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            >
              <option value="">انتخاب کنید</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.title}
                </option>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="شماره کارت"
              value={formData.cardNumber}
              onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              select
              fullWidth
              label="شیفت کاری"
              value={formData.workShift}
              onChange={(e) => setFormData({ ...formData, workShift: e.target.value as 'day' | 'night' })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
              SelectProps={{ native: true }}
              inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
            >
              <option value="day">روز</option>
              <option value="night">شب</option>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenEditModal(false)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
              انصراف
            </Button>
            <Button type="submit" variant="contained" sx={{ fontFamily: 'Vazir, sans-serif' }}>
              ذخیره تغییرات
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ManagerUsers;
